import React, { useState } from 'react';
import { Mic, Users, Calendar, Send, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const BeOurGuest = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    topic: '',
    message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Direct to email
    window.location.href = 'mailto:dosboldlatinas@gmail.com?subject=Guest Request for Dos Bold Latinas Podcast';
    toast({
      title: 'Opening Email',
      description: 'Please send us your guest request details!',
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const benefits = [
    {
      icon: Users,
      title: 'Reach Our Community',
      description: 'Connect with thousands of engaged millennial Latinas and working mamis who are part of our growing community.',
    },
    {
      icon: Mic,
      title: 'Share Your Story',
      description: 'Share your expertise, experiences, and insights with an audience that values authentic conversations.',
    },
    {
      icon: Calendar,
      title: 'Flexible Scheduling',
      description: 'We work around your schedule to make the recording process as smooth and convenient as possible.',
    },
  ];

  const idealTopics = [
    'Entrepreneurship & Business',
    'Career Development & Leadership',
    'Mental Health & Wellness',
    'Parenting & Family Life',
    'Cultural Identity & Heritage',
    'Personal Growth & Empowerment',
    'Finance & Money Management',
    'Health & Fitness',
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section 
        className="py-20 px-6 text-white relative overflow-hidden"
        style={{ backgroundColor: '#b76e79' }}
      >
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Be Our Guest
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Want to share your story on Dos Bold Latinas podcast? We'd love to have you! 
            Join us for authentic conversations and connect with our amazing community.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Introduction */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">
              Share Your Story on <span style={{ color: '#b76e79' }}>Our Podcast</span>
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed mb-8">
              Do you have an inspiring story, expertise to share, or experiences that could help 
              our community? We're always looking for amazing guests to join us for real, 
              unfiltered conversations about topics that matter.
            </p>
            <a href="mailto:dosboldlatinas@gmail.com?subject=Guest Request for Dos Bold Latinas Podcast">
              <Button 
                size="lg" 
                className="text-lg px-12 py-6 transition-all duration-300 hover:scale-105 hover:shadow-xl"
                style={{ backgroundColor: '#b76e79' }}
              >
                <Send className="mr-2" size={24} />
                Contact Us to Be a Guest
              </Button>
            </a>
          </div>

          {/* Benefits */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-center mb-12">
              Why Be a Guest on <span style={{ color: '#b76e79' }}>Our Podcast</span>?
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <Card 
                    key={index} 
                    className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                  >
                    <CardContent className="p-8 text-center">
                      <div 
                        className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
                        style={{ backgroundColor: '#b76e79' }}
                      >
                        <Icon size={32} className="text-white" />
                      </div>
                      <h4 className="text-xl font-bold mb-3">{benefit.title}</h4>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Topics We Cover */}
          <div className="mb-16">
            <Card className="border-none shadow-xl">
              <CardHeader className="text-center">
                <CardTitle className="text-3xl">
                  Topics We <span style={{ color: '#b76e79' }}>Love to Discuss</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
                  {idealTopics.map((topic, index) => (
                    <div 
                      key={index} 
                      className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <CheckCircle size={20} style={{ color: '#b76e79' }} className="shrink-0" />
                      <span className="text-gray-700 font-medium">{topic}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact CTA */}
          <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white border-none mb-16">
            <CardContent className="p-12 text-center">
              <h3 className="text-3xl font-bold mb-4">
                Ready to Share Your Story?
              </h3>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                We'd love to hear from you! Reach out to discuss being a guest on our podcast.
              </p>
              <a href="mailto:dosboldlatinas@gmail.com?subject=Guest Request for Dos Bold Latinas Podcast">
                <Button 
                  size="lg" 
                  className="text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
                  style={{ backgroundColor: '#b76e79' }}
                >
                  Email Us Your Guest Request
                </Button>
              </a>
            </CardContent>
          </Card>

          {/* Invite Us Section */}
          <Card className="border-none shadow-xl">
            <CardContent className="p-12 text-center">
              <h3 className="text-3xl font-bold mb-4">
                Want Us on <span style={{ color: '#b76e79' }}>Your Show</span>?
              </h3>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
                Jatnna and Rosibel are also available for guest appearances on other podcasts, 
                interviews, panel discussions, and speaking engagements. If you'd like us to 
                join your platform, please fill out our invitation form.
              </p>
              <a 
                href="https://docs.google.com/forms/d/e/1FAIpQLSf4HYcJOhdQiWfp2nZNJoXSJ0U8ypV17WP0hEuaYYI77O_jDw/viewform?usp=sf_link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  size="lg" 
                  variant="outline"
                  className="text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
                >
                  <Send className="mr-2" size={20} />
                  Invite Us to Your Podcast
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default BeOurGuest;